import { Component, inject, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { NgClass } from '@angular/common';
import { IconComponent } from '@eda/shared/components/icon/icon.component';
import { UserService } from '@eda/services/service.index';
import { LogoSidebar } from '@eda/configs/index';
import { CreateDashboardService } from '@eda/services/utils/create-dashboard.service';
import { GroupService } from '@eda/services/api/group.service';

interface NavItem {
  path?: string;
  icon?: string;
  isActive?: boolean;
  label?: string;
  items?: { path?: string; lang?: string; label: string; icon?: string, command?: () => void }[];
  showOverlay?: boolean;
  hideTimeout?: any;
}

@Component({
  selector: 'app-main-left-sidebar',
  standalone: true,
  imports: [RouterLink, NgClass, IconComponent],
  templateUrl: './main-left-sidebar.html',
  styleUrls: ['./main-left-sidebar.component.css'],
  encapsulation: ViewEncapsulation.None
})
export class MainLeftSidebarComponent {
  private router = inject(Router);
  private userService = inject(UserService);
  public logoSidebar = LogoSidebar;
  private createDashboardService = inject(CreateDashboardService);
  private groupService = inject(GroupService);
  public queryParams: any = {};
  public hideWheel: boolean = false;
  public panelMode: boolean = false;
  private route = inject(ActivatedRoute);
  navItems: NavItem[] = [];

  // Control de anonim y eda_RO
  public isAnonim: boolean;
  public grups: Array<any> = [];
  public isObserver: boolean = false;


  ngOnInit(): void {
    const user = JSON.parse(localStorage.getItem('user'));
    this.isAnonim = user._id === '135792467811111111111112';

    this.groupService.getGroupsByUser().subscribe(groups => {
      this.grups = groups;
      this.isObserver = groups.some((g: any) => g.name === 'EDA_RO' && g.users.includes(user._id));
      localStorage.setItem('isObserver', String(this.isObserver));
      this.assignNavItems();
    });

    this.getUrlParams();
  }

  assignNavItems() {
    // básicos para todos los users
    const baseNav: NavItem[] = [
      { path: '/home', icon: 'home' },
      {
        icon: 'settings',
        items: [
          { path: '/profile', label: $localize`:@@sidebarProfile:Perfil`, icon: 'profile' },
          { lang: 'EN', label: 'English', icon: 'en-flag' },
          { lang: 'ES', label: 'Español', icon: 'es-flag' },
          { lang: 'CA', label: 'Català', icon: 'cat-flag' },
          { lang: 'FR', label: 'Francés', icon: 'fr-flag' },
          { lang: 'PL', label: 'Polski', icon: 'pl-flag' },
        ]
      },
      {
        path: '/about',
        icon: 'global',
        label: 'Tutorial'
      },
      { path: '/logout', icon: 'logout' },
    ];


  // Seccion adicional crear (oculto para EDA_RO)
  const plusSection: NavItem = {
    icon: 'plus',
    items: [
      { path: '/home', label: $localize`:@@tituloNuevoInforme:Crear nuevo informe`, icon: 'plus', command: () => this.createDashboardService.open() },
    ]
  };

  if (this.userService.isAdmin || this.userService.isDataSourceCreator) {
    plusSection.items.push({ path: '/admin/data-source/new', label: $localize`:@@addDatasource: Crear fuente de datos`, icon: 'plus' });
  }
    

  const moleculaSection: NavItem = {
    icon: 'molecula',
    items: []
  };

  if (this.userService.isAdmin) {
    moleculaSection.items.unshift(
      { path: '/admin/users', label: $localize`:@@adminUsers:Gestión de usuarios`, icon: 'users' },
      { path: '/admin/groups', label: $localize`:@@adminGroupsTitle:Gestión de grupos`, icon: 'groups' },
      { path: '/admin/data-source', label: $localize`:@@adminDatasource:Gestión de fuentes de datos`, icon: 'rectangle-group' },
      { path: '/admin/models/import-export', label: $localize`:@@dataExportImport:Data Export/Import`, icon: 'arrow-down-on-square-stack' },
      { path: '/admin/email-settings', label: $localize`:@@adminEmail:Gestión de email`, icon: 'at-symbol' },
      { path: '/logs', label: $localize`:@@logsManagement:Gestión de logs`, icon: 'clipboard-document-list' },
      { path: '/admin/ai-settings', label: $localize`:@@AIManagement:Gestión del asistente`, icon: 'sparkles-ai' },
      { path: '/portal', label: $localize`:@@adminPortal:Portal de datos`, icon: 'global-mini' },
    );
  } else if (this.userService.isDataSourceCreator) {
    moleculaSection.items.unshift(
      { path: '/admin/data-source', label: $localize`:@@adminDatasource:Gestión de fuentes de datos`, icon: 'rectangle-group' },
    );
  }

  // Asignamos las secciones al navItems
  this.navItems = [
    ...baseNav.slice(0, 1), // home
    ...(!this.isObserver ? [plusSection] : []), // create menu (oculto para EDA_RO)
    ...((this.userService.isAdmin || this.userService.isDataSourceCreator) ? [moleculaSection] : []), // gestion
    ...baseNav.slice(1),    // settings, about, logout
  ];
  }

  showOverlay(item: NavItem) {
    if (item.hideTimeout) {
      clearTimeout(item.hideTimeout); // Cancela la ocultación si el usuario vuelve a entrar
    }
    item.showOverlay = true;
  }

  hideOverlay(item: NavItem) {
    // Usa un timeout para evitar que el overlay desaparezca instantáneamente
    item.hideTimeout = setTimeout(() => {
      item.showOverlay = false;
    }, 100); // Espera 200ms antes de ocultar
  }

menuCommand(item: any, event: MouseEvent) {
  const path = item.path || '';
  const urlTree = this.router.createUrlTree([path]);
  const relativeUrl = this.router.serializeUrl(urlTree);

  // Si es clic medio y hay ruta, abrir en nueva pestaña
  if (event.button === 1 && path) {
    event.preventDefault();
    window.open('#/' +  relativeUrl);
    return;
  }

  if (path.includes('logout')) {

    const user = JSON.parse(localStorage.getItem('user'));

    if(user.nameID && user.nameIDFormat && user.sessionIndex) {
      // LOGOUT A TRAVÉS DE SAML
      this.userService.SAMLlogout();
    } 
    else {
      // LOGOUT POR DEFAULT
      this.userService.logout();
    }

  } else if (path) {
    item.command ? item.command() : this.router.navigate([path]);
  } else if (item.lang) {
    this.redirectLocale(item.lang);

  }
}

  public redirectLocale(lan: string) {
    let baseUrl = window.location.href.split('#')[0];

    if (baseUrl.slice(-4) == '/es/' ||
        baseUrl.slice(-4) == '/ca/' ||
        baseUrl.slice(-4) == '/pl/' ||
        baseUrl.slice(-4) == '/fr/' ||
        baseUrl.slice(-4) == '/en/') {
        baseUrl = baseUrl.slice(0, baseUrl.length - 3)
    }
    switch (lan) {
      case 'EN': window.location.href = baseUrl + 'en/#/home'; break;
      case 'CA': window.location.href = baseUrl + 'ca/#/home'; break;
      case 'ES': window.location.href = baseUrl + 'es/#/home'; break;
      case 'FR': window.location.href = baseUrl + 'fr/#/home'; break;
      case 'PL'  : window.location.href = baseUrl + 'pl/#/home'; break;
    }
  }
  private getUrlParams(): void {
        this.route.queryParams.subscribe(params => {
          this.queryParams = params;
          try{
            if(params['hideWheel'] == 'true'){
              this.hideWheel =true;
            }
            if(params['panelMode'] == 'true'){
              this.panelMode =true;
              this.hideWheel =true;
            }
            
          } catch(e){
            console.error('getUrlParams: '+ e);
          }
        });
    }
}